// registro.ts — l'elenco dei moduli. L'UNICO file da toccare per
// aggiungerne uno: barra, router, sync e home lo prendono da qui.
//
// Stessi id, stesso ordine e stessi gruppi della app di prima: gli id sono
// gli indirizzi delle rotte (`#/finanze`) e delle notifiche, e cambiarne uno
// romperebbe il tocco su una notifica arrivata ieri.
//
// OGNI MODULO HA DUE PARTI, e si caricano separatamente:
//
//   vista      il componente Svelte della schermata. Arriva quando apri la
//              scheda: l'avvio non paga Finanze se apri Pasti.
//   contratto  `moduli/<id>/contratto.js`, CONDIVISO con la app di prima:
//              il canale di sync, `oggi()` per la home, le azioni che la
//              home può chiedere (spuntare un'abitudine). Una copia sola,
//              così le regole di fusione dei dati non possono divergere fra
//              le due app.

import type { Component } from "svelte";
import { avviaSync as sincronizzaContesto } from "./contesto";
import { avviaSync as sincronizzaNotifiche } from "./notifiche";

/** La scheda che un modulo dà alla home. Vedi CLAUDE.md, «oggi()». */
export interface SchedaOggi {
  titolo?: string;
  valore?: string | number | null;
  dettaglio?: string | null;
  urgente?: boolean;
  fatto?: boolean;
  azione?: { rotta?: string; etichetta?: string };
  /** Una sfida a giorni (Project 50): la home ne fa una card a parte,
      perché si misura in giorni e non nelle spunte del modulo. */
  sfida?: SchedaSfida | null;
  /** In che momento della giornata siamo, secondo il modulo che possiede le
      fasce. La home lo usa per dire «a posto per la mattina» quando la
      fascia in corso è finita: le ore stanno nel modulo, e una seconda
      copia qui mentirebbe il giorno in cui qualcuno le sposta. */
  fasciaOra?: { id: string; nome: string } | null;
  /** Come si chiamano le fasce, per tradurre la `fascia` di una voce.
      Serve perché `nomeFascia` NON è il nome del momento: è l'etichetta di
      coda della riga, e un modulo ci mette la durata («14 min»). */
  fasce?: { id: string; nome: string }[] | null;
  [k: string]: any;
}

/** Quello che la home deve sapere di una sfida senza conoscerne il modulo. */
export interface SchedaSfida {
  giorno: number;
  totale: number;
  fatte: number;
  previste: number;
  /** Una per ognuna delle voci previste oggi, nell'ordine della sfida. */
  pallini?: { nome: string; fatta: boolean }[];
  completo: boolean;
  nomiMancate: string[];
  chiuso: boolean;
  esito: "ok" | "no" | null;
  prossimo: number | null;
  daChiudere: boolean;
  dalleOre: number;
  urgente: boolean;
  rotta: string;
}

export interface Contratto {
  avviaSync?: (opz?: { ridisegna?: () => void }) => unknown;
  oggi?: () => SchedaOggi | null;
  [k: string]: any;
}

export interface VoceModulo {
  id: string;
  nome: string;
  icona: string;
  /** Il colore del modulo: dice DOVE SEI e colora le azioni. */
  accento: string;
  gruppo?: string;
  /** Da che parte del bersaglio si sta bene: in Finanze stare sotto, in
      Training stare sopra. La home non può indovinarlo. */
  verso?: "su" | "giu";
  vista: () => Promise<{ default: Component<any> }>;
  contratto?: () => Promise<Contratto>;
  /** La sua pagina in Impostazioni. La disegna il modulo, non Impostazioni:
      così Impostazioni non importa nessun modulo (CLAUDE.md, regola 12). */
  impostazioni?: () => Promise<{ default: Component<any> }>;
  /** IN PAUSA: resta nel registro ma non esiste per la app.
   *
   *  Non si cancella un modulo togliendo la sua riga da qui: sparirebbero
   *  anche la rotta — e un segnalibro o una notifica vecchia aprirebbero il
   *  vuoto — e il ricordo di com'era configurato. Un modulo in pausa non
   *  compare nella barra, non ha una carta in home, non viene interrogato
   *  per `oggi()` e non apre il suo canale di sync. La sua SCHERMATA resta
   *  raggiungibile dalla rotta, e i suoi dati restano nel repo dove sono.
   *
   *  Per riaccenderlo si toglie questa riga, e torna tutto. */
  pausa?: boolean;
}

export const MODULI: VoceModulo[] = [
  {
    id: "oggi", nome: "Oggi", icona: "oggi", accento: "var(--color-blue)",
    vista: () => import("../../moduli/oggi/Vista.svelte"),
  },
  {
    id: "finanze", nome: "Finanze", icona: "finanze", accento: "var(--color-orange)", verso: "giu",
    vista: () => import("../../moduli/finanze/Vista.svelte"),
    contratto: () => import("$condivisi/finanze/contratto.js"),
    impostazioni: () => import("../../moduli/finanze/Impostazioni.svelte"),
  },
  {
    id: "pasti", nome: "Pasti", icona: "pasti", accento: "var(--color-pink)", verso: "giu",
    vista: () => import("../../moduli/pasti/Vista.svelte"),
    contratto: () => import("$condivisi/pasti/contratto.js"),
    impostazioni: () => import("../../moduli/pasti/Impostazioni.svelte"),
  },
  {
    id: "mobilita", nome: "Mobilità", icona: "corpo", accento: "var(--color-mint)", gruppo: "corpo",
    vista: () => import("../../moduli/mobilita/Vista.svelte"),
    contratto: () => import("$condivisi/mobilita/contratto.js"),
    impostazioni: () => import("../../moduli/mobilita/Impostazioni.svelte"),
  },
  {
    id: "allenamenti", nome: "Training", icona: "bersaglio", accento: "var(--color-red)", gruppo: "corpo",
    vista: () => import("../../moduli/allenamenti/Vista.svelte"),
    contratto: () => import("$condivisi/allenamenti/contratto.js"),
    impostazioni: () => import("../../moduli/allenamenti/Impostazioni.svelte"),
  },
  {
    /* DI NUOVO «ABITUDINI», dall'11 ottobre 2026, e senza Project 50.

       Era in pausa dall'8: la sfida chiedeva otto spunte al giorno e
       ricominciava da capo a ogni dimenticanza — una cosa che o la fai
       intera o ti dice tutti i giorni che hai sbagliato. Torna come ai
       vecchi tempi: le abitudini scelte, con i loro orari, e niente
       contatore che riparte da uno. La sfida è spenta da un interruttore
       solo (`SOSPESA` in p50.js) e i suoi dati restano dove sono. */
    id: "abitudini", nome: "Abitudini", icona: "abitudini", accento: "var(--color-indigo)",
    vista: () => import("../../moduli/abitudini/Vista.svelte"),
    contratto: () => import("$condivisi/abitudini/contratto.js"),
    impostazioni: () => import("../../moduli/abitudini/Impostazioni.svelte"),
  },
  {
    id: "impostazioni", nome: "Impostazioni", icona: "impostazioni", accento: "var(--color-blue)",
    vista: () => import("../../moduli/impostazioni/Vista.svelte"),
  },
];

/* I GRUPPI: due moduli, una scheda sola nella barra. Mobilità e Training
   raccontano la stessa cosa da due lati — il corpo, e un numero da colpire
   entro dicembre — e meritano due archivi ma non due schede su cinque.
   Il gruppo è SOLO un fatto della barra: ogni membro tiene casella, canale,
   rotte e scheda nella home. */
export const GRUPPI = [
  { id: "corpo", nome: "Corpo", icona: "corpo", membri: ["mobilita", "allenamenti"] },
];

export const voceDi = (id: string) => MODULI.find((m) => m.id === id);
export const gruppoDi = (id: string) => GRUPPI.find((g) => g.membri.includes(id)) ?? null;

/* Quale membro aprire toccando la scheda del gruppo: l'ultimo guardato. È
   una preferenza di questo dispositivo, come il tema — non un dato, quindi
   niente sync. Stessa chiave della app di prima. */
const chiaveMembro = (idGruppo: string) => `atlas.gruppo.${idGruppo}`;

export function membroRicordato(gruppo: { id: string; membri: string[] }) {
  try {
    const v = localStorage.getItem(chiaveMembro(gruppo.id));
    if (v && gruppo.membri.includes(v)) return v;
  } catch { /* privata o piena: si riparte dal primo */ }
  return gruppo.membri[0];
}

export function ricordaMembro(idGruppo: string, idModulo: string) {
  try { localStorage.setItem(chiaveMembro(idGruppo), idModulo); } catch { /* vedi sopra */ }
}

/* Le voci della barra: i moduli senza gruppo più una voce per gruppo.
   Impostazioni resta fuori — si apre dall'ingranaggio della home. A sei
   schede le etichette diventano illeggibili proprio dove il pollice ha meno
   spazio: cinque è il tetto. */
export interface VoceBarra { id: string; nome: string; icona: string; rotte: string[]; gruppo?: (typeof GRUPPI)[number] }

export const MODULI_IN_BARRA: VoceBarra[] = (() => {
  const visti = new Set<string>();
  const voci: VoceBarra[] = [];
  for (const m of MODULI) {
    if (m.id === "impostazioni" || m.pausa) continue;
    const g = m.gruppo ? GRUPPI.find((x) => x.id === m.gruppo) : undefined;
    if (!g) { voci.push({ id: m.id, nome: m.nome, icona: m.icona, rotte: [m.id] }); continue; }
    if (visti.has(g.id)) continue;
    visti.add(g.id);
    voci.push({ id: g.id, nome: g.nome, icona: g.icona, rotte: g.membri, gruppo: g });
  }
  return voci;
})();

/** I moduli con dati propri: quelli che hanno un contratto e sono accesi.

    Un modulo in pausa non viene interrogato: niente carta in home, niente
    voci nella giornata, niente canale di sync aperto. Il suo file nel repo
    dati resta dov'è — in pausa vuol dire spento, non cancellato. */
export const MODULI_DATI = MODULI.filter((m) => m.contratto && !m.pausa);

/** Quelli in pausa, per chi deve dirlo: Impostazioni. */
export const MODULI_IN_PAUSA = MODULI.filter((m) => m.pausa);

const contratti = new Map<string, Contratto>();

/** Carica (una volta sola) il contratto di un modulo. */
export async function prendiContratto(id: string): Promise<Contratto | null> {
  const gia = contratti.get(id);
  if (gia) return gia;
  const voce = voceDi(id);
  if (!voce?.contratto) return null;
  const c = await voce.contratto();
  contratti.set(id, c);
  return c;
}

/**
 * Apre TUTTI i canali di sync, compreso quello della lavagna. Per tutti i
 * moduli e non solo quello aperto: i dati di Finanze devono arrivare anche
 * mentre guardi Pasti, altrimenti la home mostra numeri vecchi.
 *
 * Un modulo rotto non deve portarsi via gli altri: ognuno nel suo try.
 */
export async function avviaTuttiISync() {
  try { sincronizzaContesto(); }
  catch (e) { console.error("[registro] sync della lavagna non avviato", e); }
  // Le iscrizioni push e gli orari: stanno nel nucleo, non in un modulo,
  // perché sono una cosa sola per tutti (CLAUDE.md, §5).
  try { sincronizzaNotifiche(); }
  catch (e) { console.error("[registro] sync delle notifiche non avviato", e); }

  const esiti = await Promise.allSettled(MODULI_DATI.map((m) => prendiContratto(m.id)));
  esiti.forEach((e, i) => {
    if (e.status !== "fulfilled" || !e.value?.avviaSync) return;
    try { e.value.avviaSync(); }
    catch (err) { console.error(`[registro] sync di "${MODULI_DATI[i].id}" non avviato`, err); }
  });
}
